import { useState } from 'react';
import type {
  AracDetay, Hizmet, ParaBirimi, StokHareketi, StokSatisSonucu, StokSatisTahsilat, Urun,
} from '@nero/shared';
import {
  ANA_PARA_BIRIMI, ODEME_TIPI_ADI, ODEME_TIPLERI, STOK_SEBEP_ADI,
  bugun, kurus, kuruBul, para, paraBirimiSimgesi, sayi, tarihSaatGoster,
} from '@nero/shared';
import { useKur } from '../lib/kur.js';
import { api, useEylem, useVeri } from '../lib/veri.js';
import { useOturum } from '../lib/oturum.js';
import { useBildirim } from '../lib/bildirim.js';
import { sorgu } from '../lib/api.js';
import { BosDurum, HataKutusu, Kart, Rozet, SayfaBaslik, UyariSerit, Yukleniyor } from '../bilesenler/temel.js';
import { Arama, Girdi, MetinAlani, Onay, ParaGirdi, Secim, SecenekSerit } from '../bilesenler/form.js';
import { Pencere } from '../bilesenler/Pencere.js';
import { MusteriSecici } from '../bilesenler/MusteriSecici.js';
import { Simge } from '../bilesenler/Simge.js';

export function Stok() {
  const [sekme, setSekme] = useState<'urun' | 'hizmet' | 'hareket'>('urun');
  const [arama, setArama] = useState('');
  const [suzgec, setSuzgec] = useState<'tumu' | 'kritik' | 'tukenen'>('tumu');
  const [duzenlenen, setDuzenlenen] = useState<Urun | 'yeni' | null>(null);
  const [duzeltilen, setDuzeltilen] = useState<Urun | null>(null);
  const [satilan, setSatilan] = useState<Urun | null>(null);
  const [hizmetDuzenlenen, setHizmetDuzenlenen] = useState<Hizmet | 'yeni' | null>(null);
  const { yetkili } = useOturum();
  const bildir = useBildirim();
  const duzenleyebilir = yetkili('stok.duzenle');
  /* SATIŞ: stoğu hareket ettirme YANINDA kasaya kayıt yazma izni de ister.
     Sunucu aynı ikiliyi arıyor; burada düğmeyi gizlemek yalnız gereksiz
     bir hata mesajını önler. */
  const satabilir = duzenleyebilir && (yetkili('kasa.islem') || yetkili('kasa.kayit'));
  /* Hareket listesindeki TUTAR sütunu kasa defterinin bir parçasıdır:
     "Ahmet Bey 12.000 ₺'lik parça aldı" satırlarını alt alta okumak,
     kapattığımız kasa defterini arka kapıdan açmak olurdu. Müşteri adı
     ise stok bilgisidir — personel zaten Müşteriler ekranını görüyor. */
  const tutarGorur = yetkili('kasa.goruntule');
  /* Alış fiyatı ve stok değeri kâr marjını ele verir: yalnızca yöneticide.
     Sunucu bu alanları zaten göndermiyor; burada sütun/alan da gizleniyor. */
  const maliyetGorur = yetkili('stok.maliyet');

  const urunler = useVeri<Urun[]>(sekme === 'urun' ? `/urunler${sorgu({ q: arama })}` : null);
  const hizmetler = useVeri<Hizmet[]>(sekme === 'hizmet' ? '/hizmetler' : null);
  const hareketler = useVeri<Array<StokHareketi & { urun_ad: string }>>(sekme === 'hareket' ? '/stok-hareketleri' : null);

  /* Sekme sayaçları ve süzgeç. Kritik = kritik seviyenin altına inmiş,
     tükenen = adedi sıfır ya da altında olan ürün. */
  const kritikMi = (u: Urun) => u.kritik_seviye > 0 && u.adet <= u.kritik_seviye;
  const sayilar = urunler.veri
    ? {
        tumu: urunler.veri.length,
        kritik: urunler.veri.filter((u) => kritikMi(u) && u.adet > 0).length,
        tukenen: urunler.veri.filter((u) => u.adet <= 0).length,
      }
    : null;
  /* Hızlı düzeltme: tek tıkla bir adet artır/azalt. Her tıklama stok
     hareketi ve denetim kaydı oluşturur — sessiz değişiklik olmaz. */
  const hizliDuzelt = useEylem(async ({ urun, degisim }: { urun: Urun; degisim: number }) => {
    await api.gonder(`/urunler/${urun.id}/stok`, {
      degisim,
      /* Sebep gönderilmezse hepsi "Sayım düzeltmesi" olarak kaydediliyordu;
         hareket geçmişinde giriş ile çıkış ayırt edilemiyordu. */
      sebep: degisim > 0 ? 'ekle' : 'cikar',
      not: 'Hızlı düzeltme',
    });
    urunler.yenile();
    bildir(`${urun.ad}: ${degisim > 0 ? '+1' : '−1'}`);
  });

  const gosterilenUrunler = (urunler.veri ?? []).filter((u) => (
    suzgec === 'tumu' ? true : suzgec === 'tukenen' ? u.adet <= 0 : kritikMi(u) && u.adet > 0
  ));

  return (
    <div className="sayfa">
      <SayfaBaslik baslik="Stok" alt="Ürünler, hizmetler ve stok hareketleri">
        {duzenleyebilir && sekme === 'urun' && (
          <button className="btn btn-birincil" onClick={() => setDuzenlenen('yeni')}>
            <Simge ad="arti" className="simge" /> Ürün ekle
          </button>
        )}
        {yetkili('fiyat.duzenle') && sekme === 'hizmet' && (
          <button className="btn btn-birincil" onClick={() => setHizmetDuzenlenen('yeni')}>
            <Simge ad="arti" className="simge" /> Hizmet ekle
          </button>
        )}
      </SayfaBaslik>

      <div className="satir" style={{ marginBottom: 'var(--b4)', flexWrap: 'wrap', gap: 'var(--b3)' }}>
        <SecenekSerit
          deger={sekme}
          secenekler={[
            { deger: 'urun', ad: 'Ürünler' },
            { deger: 'hizmet', ad: 'Hizmetler' },
            { deger: 'hareket', ad: 'Hareketler' },
          ]}
          onChange={setSekme}
        />
        {sekme === 'urun' && <Arama deger={arama} onChange={setArama} yerTutucu="Ürün, marka, kategori…" />}
      </div>

      {sekme === 'urun' && (
        <div className="satir" style={{ marginBottom: 'var(--b4)' }}>
          <SecenekSerit
            deger={suzgec}
            secenekler={[
              { deger: 'tumu' as const, ad: `Tüm ürünler${sayilar ? ` · ${sayilar.tumu}` : ''}` },
              { deger: 'kritik' as const, ad: `Kritik seviyede${sayilar ? ` · ${sayilar.kritik}` : ''}` },
              { deger: 'tukenen' as const, ad: `Tükenenler${sayilar ? ` · ${sayilar.tukenen}` : ''}` },
            ]}
            onChange={setSuzgec}
          />
        </div>
      )}

      <Kart>
        {sekme === 'urun' && (
          urunler.hata ? <div className="kart-govde"><HataKutusu hata={urunler.hata} yenile={urunler.yenile} /></div>
          : urunler.yukleniyor ? <div className="kart-govde"><Yukleniyor /></div>
          : !urunler.veri?.length ? (
            arama.trim()
              ? <BosDurum simge="stok" baslik="Sonuç yok"
                  aciklama={`"${arama}" için ürün bulunamadı. Farklı bir ad, marka veya kategori deneyin.`} />
              : <BosDurum simge="stok" baslik="Ürün yok"
                  aciklama="Sattığınız parçaları ekleyerek stok takibini başlatın." />
          )
          : gosterilenUrunler.length === 0 ? (
            <BosDurum
              simge="stok"
              baslik={suzgec === 'tukenen' ? 'Tükenen ürün yok' : 'Kritik seviyede ürün yok'}
              aciklama="Stok durumu iyi görünüyor."
            />
          )
          : (
            <div className="tablo-sarma">
              <table className="tablo">
                <thead>
                  <tr>
                    <th>Ürün</th><th>Kategori</th>
                    <th className="sag">Stok</th>
                    {maliyetGorur && <th className="sag">Alış</th>}
                    <th className="sag">Satış</th>
                    {duzenleyebilir && <th />}
                  </tr>
                </thead>
                <tbody>
                  {gosterilenUrunler.map((u) => {
                    const kritik = kritikMi(u);
                    return (
                      <tr key={u.id}>
                        <td>
                          <span className="ana">{u.ad}</span>
                          {u.pasif === 1 && <> <Rozet ton="notr" sade>Pasif</Rozet></>}
                          {u.marka && <div className="ikincil">{u.marka}</div>}
                        </td>
                        <td className="ikincil">{u.kategori || '—'}</td>
                        <td className="sag sayi">
                          <span style={{ color: kritik ? 'var(--uyari)' : undefined, fontWeight: kritik ? 600 : 400 }}>
                            {sayi(u.adet)} {u.birim}
                          </span>
                          {kritik && <div style={{ fontSize: 'var(--p-11)', color: 'var(--uyari)' }}>kritik</div>}
                        </td>
                        {/* DÖVİZLİ ÜRÜNDE ÖNCE YAZILAN FİYAT GÖRÜNÜR.
                            "120 €'ya aldım" diyen kullanıcı listede 120 €
                            arar; TL karşılığı altında küçük durur. TL
                            ürünlerde ikinci satır HİÇ çıkmaz. */}
                        {maliyetGorur && (
                          <td className="sag para ikincil">
                            {para(u.alis_doviz ?? u.alis_fiyat ?? 0, u.alis_para_birimi)}
                            {(u.alis_para_birimi ?? ANA_PARA_BIRIMI) !== ANA_PARA_BIRIMI && (
                              <div className="doviz-alt">{para(u.alis_fiyat ?? 0)}</div>
                            )}
                          </td>
                        )}
                        <td className="sag para">
                          {para(u.satis_doviz ?? u.satis_fiyat, u.satis_para_birimi)}
                          {(u.satis_para_birimi ?? ANA_PARA_BIRIMI) !== ANA_PARA_BIRIMI && (
                            <div className="ikincil doviz-alt">{para(u.satis_fiyat)}</div>
                          )}
                        </td>
                        {duzenleyebilir && (
                          <td className="dar">
                            <div className="satir">
                              <button
                                className="btn btn-sade btn-simge btn-kucuk" aria-label="Bir azalt"
                                disabled={hizliDuzelt.calisiyor || u.adet <= 0}
                                onClick={() => void hizliDuzelt.yurut({ urun: u, degisim: -1 })}
                              >−</button>
                              <button
                                className="btn btn-sade btn-simge btn-kucuk" aria-label="Bir artır"
                                disabled={hizliDuzelt.calisiyor}
                                onClick={() => void hizliDuzelt.yurut({ urun: u, degisim: 1 })}
                              >+</button>
                              {/* SATIŞ — "kim aldı, kaça, ne kadarı cariye".
                                  − düğmesi yerinde duruyor: o, kırılan ya da
                                  içeride kullanılan parça içindir ve parayla
                                  ilgisi yoktur. */}
                              {satabilir && (
                                <button
                                  className="btn btn-sade btn-kucuk"
                                  disabled={u.adet <= 0}
                                  title={u.adet <= 0 ? 'Stokta yok' : 'Müşteriye sat'}
                                  onClick={() => setSatilan(u)}
                                >Satış</button>
                              )}
                              <button className="btn btn-sade btn-kucuk" onClick={() => setDuzeltilen(u)}>Sayım</button>
                              <button className="btn btn-sade btn-simge btn-kucuk" onClick={() => setDuzenlenen(u)} aria-label="Düzenle">
                                <Simge ad="kalem" boyut={14} />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}

        {sekme === 'hizmet' && (
          hizmetler.hata ? <div className="kart-govde"><HataKutusu hata={hizmetler.hata} yenile={hizmetler.yenile} /></div>
          : hizmetler.yukleniyor ? <div className="kart-govde"><Yukleniyor /></div>
          : !hizmetler.veri?.length ? <BosDurum simge="fiyat" baslik="Hizmet yok" />
          : (
            <div className="tablo-sarma">
              <table className="tablo">
                <thead><tr><th>Hizmet</th><th>Kategori</th><th className="sag">Varsayılan fiyat</th><th>Durum</th>{yetkili('fiyat.duzenle') && <th />}</tr></thead>
                <tbody>
                  {hizmetler.veri.map((h) => (
                    <tr key={h.id}>
                      <td className="ana">{h.ad}</td>
                      <td className="ikincil">{h.kategori || '—'}</td>
                      <td className="sag para">{h.varsayilan_fiyat != null ? para(h.varsayilan_fiyat) : '—'}</td>
                      <td><Rozet ton={h.aktif ? 'olumlu' : 'notr'}>{h.aktif ? 'Aktif' : 'Pasif'}</Rozet></td>
                      {yetkili('fiyat.duzenle') && (
                        <td className="dar">
                          <button className="btn btn-sade btn-simge btn-kucuk" onClick={() => setHizmetDuzenlenen(h)} aria-label="Düzenle">
                            <Simge ad="kalem" boyut={14} />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {sekme === 'hareket' && (
          hareketler.hata ? <div className="kart-govde"><HataKutusu hata={hareketler.hata} yenile={hareketler.yenile} /></div>
          : hareketler.yukleniyor ? <div className="kart-govde"><Yukleniyor /></div>
          : !hareketler.veri?.length ? <BosDurum simge="gecmis" baslik="Stok hareketi yok" />
          : (
            <div className="tablo-sarma">
              <table className="tablo">
                <thead>
                  <tr>
                    <th>Tarih</th><th>Ürün</th><th>Sebep</th>
                    <th className="sag">Değişim</th><th className="sag">Önceki</th><th className="sag">Yeni</th>
                    {/* KİM ALDI — satışta dolu, diğer hareketlerde boş. */}
                    <th>Müşteri</th>
                    {/* KAÇA — para bilgisi kasa defterinin parçasıdır;
                        personel kasa toplamlarını görmediği gibi bu sütunu
                        da görmez (bkz. izinler.ts · kasa.goruntule). */}
                    {tutarGorur && <th className="sag">Tutar</th>}
                    <th>Kullanıcı</th><th>Not</th>
                  </tr>
                </thead>
                <tbody>
                  {hareketler.veri.map((h) => (
                    <tr key={h.id}>
                      <td className="ikincil dar">{tarihSaatGoster(h.created_at)}</td>
                      <td className="ana">{h.urun_ad}</td>
                      <td>
                        <Rozet ton="notr" sade>{STOK_SEBEP_ADI[h.sebep] ?? h.sebep}</Rozet>
                        {h.is_emri_no && <div className="ikincil">{h.is_emri_no}</div>}
                      </td>
                      <td className="sag sayi" style={{ color: h.degisim > 0 ? 'var(--olumlu)' : 'var(--uyari)' }}>
                        {h.degisim > 0 ? '+' : ''}{sayi(h.degisim)}
                      </td>
                      <td className="sag sayi ikincil">{h.onceki == null ? '—' : sayi(h.onceki)}</td>
                      <td className="sag sayi">{sayi(h.kalan)}</td>
                      <td className="ikincil">{h.musteri_ad ?? '—'}</td>
                      {tutarGorur && (
                        <td className="sag para">
                          {h.tutar == null ? <span className="ikincil">—</span> : (
                            <>
                              {para(
                                (h.para_birimi ?? ANA_PARA_BIRIMI) !== ANA_PARA_BIRIMI && h.doviz_birim_fiyat != null
                                  ? kurus(Math.abs(h.degisim) * h.doviz_birim_fiyat)
                                  : h.tutar,
                                h.para_birimi,
                              )}
                              {(h.para_birimi ?? ANA_PARA_BIRIMI) !== ANA_PARA_BIRIMI && (
                                <div className="ikincil doviz-alt">{para(h.tutar)}</div>
                              )}
                            </>
                          )}
                        </td>
                      )}
                      <td className="ikincil">{h.kullanici_ad ?? '—'}</td>
                      <td className="ikincil">{h.not || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </Kart>

      {duzenlenen && (
        <UrunPenceresi
          mevcut={duzenlenen === 'yeni' ? undefined : duzenlenen}
          kapat={() => setDuzenlenen(null)}
          kaydedildi={() => { setDuzenlenen(null); urunler.yenile(); }}
        />
      )}
      {duzeltilen && (
        <StokDuzeltPenceresi
          urun={duzeltilen}
          kapat={() => setDuzeltilen(null)}
          kaydedildi={() => { setDuzeltilen(null); urunler.yenile(); }}
        />
      )}
      {satilan && (
        <SatisPenceresi
          urun={satilan}
          kapat={() => setSatilan(null)}
          kaydedildi={() => { setSatilan(null); urunler.yenile(); }}
        />
      )}
      {hizmetDuzenlenen && (
        <HizmetPenceresi
          mevcut={hizmetDuzenlenen === 'yeni' ? undefined : hizmetDuzenlenen}
          kapat={() => setHizmetDuzenlenen(null)}
          kaydedildi={() => { setHizmetDuzenlenen(null); hizmetler.yenile(); }}
        />
      )}
    </div>
  );
}

function UrunPenceresi({ mevcut, kapat, kaydedildi }: { mevcut?: Urun; kapat: () => void; kaydedildi: () => void }) {
  const { yetkili } = useOturum();
  const maliyetGorur = yetkili('stok.maliyet');
  const bildir = useBildirim();
  /* DÜZENLEMEDE YAZILAN SAYI GERİ GELİR, TL karşılığı DEĞİL.
     120 €'ya alınmış bir ürünü açınca 6.000 görünseydi, kullanıcı hiçbir
     şey değiştirmeden kaydettiğinde o 6.000 bir kez daha kurla çarpılır
     ve maliyet 50 katına çıkardı. */
  const [d, setD] = useState({
    ad: mevcut?.ad ?? '', marka: mevcut?.marka ?? '', kategori: mevcut?.kategori ?? '',
    alis_fiyat: String(mevcut?.alis_doviz ?? mevcut?.alis_fiyat ?? ''),
    satis_fiyat: String(mevcut?.satis_doviz ?? mevcut?.satis_fiyat ?? ''),
    adet: String(mevcut?.adet ?? '0'), birim: mevcut?.birim ?? 'adet',
    kritik_seviye: String(mevcut?.kritik_seviye ?? '0'), not: mevcut?.not ?? '',
  });
  const [alisBirimi, setAlisBirimi] = useState<ParaBirimi>(mevcut?.alis_para_birimi ?? ANA_PARA_BIRIMI);
  const [satisBirimi, setSatisBirimi] = useState<ParaBirimi>(mevcut?.satis_para_birimi ?? ANA_PARA_BIRIMI);
  const [pasif, setPasif] = useState(mevcut?.pasif === 1);
  const yaz = (k: keyof typeof d) => (v: string) => setD((o) => ({ ...o, [k]: v }));

  const { yurut, calisiyor, hata } = useEylem(async () => {
    const govde = {
      ...d,
      /* Fiyatlar KULLANICININ YAZDIĞI sayılardır; TL karşılığını sunucu
         hesaplar. Alışın kuru yazıldığı anda donar, satışınki canlıdır
         (bkz. depo/stok.ts · parametreler). */
      alis_fiyat: Number(d.alis_fiyat) || 0,
      alis_para_birimi: alisBirimi,
      satis_fiyat: Number(d.satis_fiyat) || 0,
      satis_para_birimi: satisBirimi,
      /* Mevcut üründe adet değiştirilemez; sunucu farklı bir değeri reddeder. */
      adet: mevcut ? Number(mevcut.adet) : Number(d.adet) || 0,
      kritik_seviye: Number(d.kritik_seviye) || 0,
      pasif: pasif ? 1 : 0,
    };
    if (mevcut) await api.guncelle(`/urunler/${mevcut.id}`, govde);
    else await api.gonder('/urunler', govde);
    bildir(mevcut ? 'Ürün güncellendi' : 'Ürün eklendi');
    kaydedildi();
  });

  return (
    <Pencere
      baslik={mevcut ? 'Ürünü düzenle' : 'Yeni ürün'}
      kapat={kapat}
      altBolum={
        <>
          <button className="btn btn-sade" onClick={kapat} disabled={calisiyor}>Vazgeç</button>
          <button className="btn btn-birincil" onClick={() => void yurut(undefined as never)}
            disabled={calisiyor || !d.ad.trim()}>Kaydet</button>
        </>
      }
    >
      <div className="sutun">
        <Girdi etiket="Ürün adı" value={d.ad} onChange={yaz('ad')} autoFocus required duzen="urunAdi" />
        <div className="izgara izgara-2">
          <Girdi etiket="Marka" value={d.marka} onChange={yaz('marka')} duzen="markaModel" />
          <Girdi etiket="Kategori" value={d.kategori} onChange={yaz('kategori')} duzen="kategoriAdi" />
          {maliyetGorur && (
            <ParaGirdi
              etiket="Alış fiyatı" deger={d.alis_fiyat} onChange={yaz('alis_fiyat')}
              paraBirimi={alisBirimi} onParaBirimi={setAlisBirimi}
            />
          )}
          <ParaGirdi
            etiket="Satış fiyatı" deger={d.satis_fiyat} onChange={yaz('satis_fiyat')}
            paraBirimi={satisBirimi} onParaBirimi={setSatisBirimi}
          />
          {mevcut ? (
            <Girdi
              etiket="Stok adedi" value={`${sayi(mevcut.adet)} ${mevcut.birim}`} readOnly disabled
              ipucu="Adet yalnızca stok hareketiyle değişir — listedeki “Sayım” düğmesini ya da +/− tuşlarını kullanın"
            />
          ) : (
            <Girdi
              etiket="Açılış stoğu" type="number" step="any" min={0} value={d.adet} onChange={yaz('adet')}
              ipucu="Elinizdeki mevcut miktar; açılış hareketi olarak kaydedilir"
            />
          )}
          <Girdi etiket="Birim" value={d.birim} onChange={yaz('birim')} />
          <Girdi etiket="Kritik seviye" type="number" step="any" value={d.kritik_seviye} onChange={yaz('kritik_seviye')}
            ipucu="Bu sayının altına düşünce ana sayfada uyarı çıkar" />
        </div>
        <Onay etiket="Pasif (listelerde gösterilmesin)" isaretli={pasif} onChange={setPasif} />
        <MetinAlani etiket="Not" value={d.not} onChange={yaz('not')} rows={2} duzen="cumle" />
        {hata && <UyariSerit ton="kritik">{hata}</UyariSerit>}
      </div>
    </Pencere>
  );
}

function StokDuzeltPenceresi({ urun, kapat, kaydedildi }: { urun: Urun; kapat: () => void; kaydedildi: () => void }) {
  const bildir = useBildirim();
  const [sebep, setSebep] = useState<'ekle' | 'cikar' | 'duzeltme'>('ekle');
  const [miktar, setMiktar] = useState('');
  const [not, setNot] = useState('');

  /* Personel eksi sayı yazmak zorunda kalmasın: sebep yönü belirler.
     "Düzeltme"de sayım sonucu girilir, fark otomatik hesaplanır. */
  const mevcutAdet = Number(urun.adet) || 0;
  const girilen = Number(miktar) || 0;
  const degisim = sebep === 'ekle' ? girilen
    : sebep === 'cikar' ? -girilen
    : girilen - mevcutAdet;

  const { yurut, calisiyor, hata } = useEylem(async () => {
    await api.gonder(`/urunler/${urun.id}/stok`, { degisim, sebep, not });
    bildir('Stok hareketi kaydedildi');
    kaydedildi();
  });

  const sonuc = mevcutAdet + degisim;

  return (
    <Pencere
      baslik="Stok düzelt"
      alt={`${urun.ad} · mevcut ${sayi(urun.adet)} ${urun.birim}`}
      kapat={kapat}
      altBolum={
        <>
          <button className="btn btn-sade" onClick={kapat} disabled={calisiyor}>Vazgeç</button>
          <button className="btn btn-birincil" onClick={() => void yurut(undefined as never)}
            disabled={calisiyor || !miktar || degisim === 0}>Uygula</button>
        </>
      }
    >
      <div className="sutun">
        <SecenekSerit
          deger={sebep}
          secenekler={[
            { deger: 'ekle' as const, ad: 'Stok ekle' },
            { deger: 'cikar' as const, ad: 'Stok çıkar' },
            { deger: 'duzeltme' as const, ad: 'Sayım düzeltmesi' },
          ]}
          onChange={(v) => { setSebep(v); setMiktar(''); }}
        />
        <Girdi
          etiket={sebep === 'duzeltme' ? 'Sayımda bulunan miktar' : 'Miktar'}
          type="number" step="any" min={0} value={miktar} onChange={setMiktar} autoFocus
          ipucu={miktar
            ? `Yeni stok: ${sayi(Math.max(0, sonuc))} ${urun.birim} · hareket ${degisim > 0 ? '+' : ''}${sayi(degisim)}`
            : (sebep === 'duzeltme' ? 'Raftaki gerçek miktarı yazın, fark otomatik hesaplanır' : 'Eklenecek ya da düşülecek miktar')}
        />
        <Girdi etiket="Açıklama" value={not} onChange={setNot} placeholder="Satın alma, sayım farkı, fire…" duzen="cumle" />
        <UyariSerit ton="notr">
          Her hareket önceki değer, yeni değer, sebep, kullanıcı ve saatiyle kaydedilir.
        </UyariSerit>
        {hata && <UyariSerit ton="kritik">{hata}</UyariSerit>}
      </div>
    </Pencere>
  );
}

function HizmetPenceresi({ mevcut, kapat, kaydedildi }: { mevcut?: Hizmet; kapat: () => void; kaydedildi: () => void }) {
  const bildir = useBildirim();
  const [ad, setAd] = useState(mevcut?.ad ?? '');
  const [kategori, setKategori] = useState(mevcut?.kategori ?? '');
  const [fiyat, setFiyat] = useState(mevcut?.varsayilan_fiyat != null ? String(mevcut.varsayilan_fiyat) : '');
  const [aktif, setAktif] = useState(mevcut ? mevcut.aktif === 1 : true);

  const { yurut, calisiyor, hata } = useEylem(async () => {
    const govde = { ad, kategori, varsayilan_fiyat: fiyat ? Number(fiyat) : null, aktif: aktif ? 1 : 0 };
    if (mevcut) await api.guncelle(`/hizmetler/${mevcut.id}`, govde);
    else await api.gonder('/hizmetler', govde);
    bildir(mevcut ? 'Hizmet güncellendi' : 'Hizmet eklendi');
    kaydedildi();
  });

  return (
    <Pencere
      baslik={mevcut ? 'Hizmeti düzenle' : 'Yeni hizmet'}
      kapat={kapat}
      altBolum={
        <>
          <button className="btn btn-sade" onClick={kapat} disabled={calisiyor}>Vazgeç</button>
          <button className="btn btn-birincil" onClick={() => void yurut(undefined as never)}
            disabled={calisiyor || !ad.trim()}>Kaydet</button>
        </>
      }
    >
      <div className="sutun">
        <Girdi etiket="Hizmet adı" value={ad} onChange={setAd} autoFocus required duzen="urunAdi" />
        <div className="izgara izgara-2">
          <Girdi etiket="Kategori" value={kategori} onChange={setKategori} duzen="kategoriAdi" />
          <ParaGirdi etiket="Varsayılan fiyat" deger={fiyat} onChange={setFiyat} ipucu="Boş bırakılabilir" />
        </div>
        <Onay etiket="Aktif" isaretli={aktif} onChange={setAktif} />
        {hata && <UyariSerit ton="kritik">{hata}</UyariSerit>}
      </div>
    </Pencere>
  );
}

/* ---------- Stoktan satış ---------- */

/**
 * SATIŞ PENCERESİ — "kim aldı, kaça, ne kadarı cariye"
 *
 * ─── İSTEK ───
 * "ürünü stoktan düşerken kimin aldığını, kaç TL'ye aldığını ve cariden ne
 *  kadar düştüğünü seçemiyoruz; direkt stoktan eksiliyor."
 *
 * ─── NİÇİN "−" DÜĞMESİNDEN AYRI ───
 * "−" ve "Sayım" para ile ilgisi olmayan hareketlerdir: kırılan parça,
 * içeride kullanılan malzeme, raf sayımı. Satışa onların içine bir seçenek
 * olarak eklenseydi, her stok düşüşünde müşteri ve fiyat sorulur, sıradan
 * bir fire kaydı üç ekstra alan doldurmayı gerektirirdi. Satış ayrı bir
 * düğme; ikisi de kendi işini yapmaya devam ediyor.
 *
 * ─── PENCEREDE NE OLUYOR ───
 * Kullanıcı üç şeyi seçiyor: müşteri (isteğe bağlı), adet ve birim fiyat
 * (dövizli olabilir), ödeme biçimi. Alt tarafta HER AN "ne olacağı" yazılı
 * duruyor: toplam, kasaya girecek para, cariye yazılacak borç ve satıştan
 * sonraki bakiye. Kullanıcı kaydetmeden önce sonucu görüyor.
 */
function SatisPenceresi({ urun, kapat, kaydedildi }: {
  urun: Urun; kapat: () => void; kaydedildi: () => void;
}) {
  const bildir = useBildirim();
  const { yetkili } = useOturum();
  /* Bakiye önizlemesi kasa defterinin bir parçasıdır; personel görmez. */
  const bakiyeGorur = yetkili('kasa.goruntule');

  const [adet, setAdet] = useState('1');
  /* Fiyat ürünün KENDİ para biriminde ve KENDİ yazılmış değeriyle gelir:
     150 €'ya fiyatlanmış parça "150 €" olarak açılır, TL karşılığı olarak
     değil. Kullanıcı isterse değiştirir. */
  const [birimFiyat, setBirimFiyat] = useState(String(urun.satis_doviz ?? urun.satis_fiyat ?? ''));
  const [paraBirimi, setParaBirimi] = useState<ParaBirimi>(urun.satis_para_birimi ?? ANA_PARA_BIRIMI);
  const [musteriId, setMusteriId] = useState('');
  const [aracId, setAracId] = useState('');
  const [tahsilat, setTahsilat] = useState<StokSatisTahsilat>('tam');
  const [alinan, setAlinan] = useState('');
  const [odemeTipi, setOdemeTipi] = useState<string>('Nakit');
  const [not, setNot] = useState('');

  const araclar = useVeri<AracDetay[]>(musteriId ? `/araclar${sorgu({ musteri_id: musteriId })}` : null);
  const kurKaydi = useKur();
  const cari = useVeri<{ ozet: { bakiye: number } }>(
    musteriId && bakiyeGorur ? `/musteriler/${musteriId}/cari` : null,
  );

  /* ───── Önizleme: sunucunun yapacağı hesabın AYNISI ─────
     Rakamlar sunucuda yeniden hesaplanır (istemciye güvenilmez); buradaki
     hesap yalnız kullanıcıya ne olacağını göstermek içindir. İkisi
     ayrışmasın diye kural birebir aynı: alınan tutarı `tahsilat` belirler,
     kullanıcının yazdığı sayı satış toplamını aşamaz. */
  const kur = kuruBul(kurKaydi, paraBirimi) ?? 1;
  const adetSayi = Number(adet) || 0;
  const toplamDoviz = kurus(adetSayi * (Number(birimFiyat) || 0));
  const toplamTL = kurus(toplamDoviz * kur);
  const alinanDoviz = tahsilat === 'tam' ? toplamDoviz
    : tahsilat === 'cari' ? 0
    : Math.min(kurus(Number(alinan) || 0), toplamDoviz);
  const alinanTL = kurus(alinanDoviz * kur);
  const cariyeTL = musteriId ? kurus(toplamTL - alinanTL) : 0;
  const mevcutBakiye = cari.veri?.ozet.bakiye ?? 0;

  const yeterliStok = adetSayi > 0 && adetSayi <= Number(urun.adet);
  const gecerli = yeterliStok && toplamTL > 0
    && (tahsilat !== 'cari' || !!musteriId)
    && (tahsilat !== 'kismi' || alinanDoviz > 0);

  const { yurut, calisiyor, hata } = useEylem(async () => {
    const s = await api.gonder<{ sonuc: StokSatisSonucu }>(`/urunler/${urun.id}/satis`, {
      tarih: bugun(),
      adet: adetSayi,
      birim_fiyat: Number(birimFiyat) || 0,
      para_birimi: paraBirimi,
      musteri_id: musteriId ? Number(musteriId) : null,
      arac_id: aracId ? Number(aracId) : null,
      tahsilat,
      alinan: tahsilat === 'kismi' ? Number(alinan) || 0 : 0,
      odeme_tipi: odemeTipi,
      not,
    });
    bildir(s.sonuc.cariyeYazilan > 0
      ? `Satış kaydedildi · ${para(s.sonuc.cariyeYazilan)} cariye yazıldı`
      : 'Satış kaydedildi');
    kaydedildi();
  });

  return (
    <Pencere
      baslik="Stoktan satış"
      alt={`${urun.ad} · stokta ${sayi(urun.adet)} ${urun.birim}`}
      kapat={kapat}
      altBolum={
        <>
          <button className="btn btn-sade" onClick={kapat} disabled={calisiyor}>Vazgeç</button>
          <button className="btn btn-birincil" onClick={() => void yurut(undefined as never)}
            disabled={calisiyor || !gecerli}>
            {calisiyor ? 'Kaydediliyor…' : 'Satışı kaydet'}
          </button>
        </>
      }
    >
      <div className="sutun">
        <div className="izgara izgara-2">
          <Girdi
            etiket="Adet" type="number" step="any" min={0} value={adet} onChange={setAdet} autoFocus
            hata={adetSayi > Number(urun.adet)
              ? `Stokta ${sayi(urun.adet)} ${urun.birim} var` : null}
          />
          <ParaGirdi
            etiket="Birim fiyat" deger={birimFiyat} onChange={setBirimFiyat}
            paraBirimi={paraBirimi} onParaBirimi={setParaBirimi}
          />
        </div>

        <MusteriSecici
          deger={musteriId}
          onChange={(v) => { setMusteriId(v); setAracId(''); }}
          yerTutucu="Müşteri ara — isim, telefon veya plaka… (Enter ile seç)"
          ipucu={musteriId
            ? 'Tahsil edilmeyen tutar bu müşterinin carisine yazılır'
            : 'Boş bırakılırsa TEZGÂH SATIŞI olur: cariye hiçbir şey yazılmaz, para kasaya girer'}
        />
        {musteriId && (
          <Secim
            etiket="Araç" value={aracId} onChange={setAracId} bosMetin="İsteğe bağlı"
            secenekler={(araclar.veri ?? []).map((a) => ({
              deger: String(a.id),
              ad: [a.plaka, a.marka, a.model].filter(Boolean).join(' · ') || `#${a.id}`,
            }))}
          />
        )}

        <SecenekSerit
          deger={tahsilat}
          secenekler={[
            { deger: 'tam' as const, ad: 'Tamamı tahsil edildi' },
            { deger: 'kismi' as const, ad: 'Kısmi' },
            { deger: 'cari' as const, ad: 'Tamamı cariye' },
          ]}
          onChange={(v) => { setTahsilat(v); if (v !== 'kismi') setAlinan(''); }}
        />
        {tahsilat === 'cari' && !musteriId && (
          <UyariSerit ton="uyari">Cariye yazmak için önce müşteri seçin.</UyariSerit>
        )}

        <div className="izgara izgara-2">
          {tahsilat === 'kismi' && (
            <ParaGirdi
              etiket="Şimdi alınan" deger={alinan} onChange={setAlinan}
              /* Alınan tutar satışla AYNI para biriminde: aynı satışta iki
                 farklı para birimi karıştırmak hem kullanıcının kafasını
                 hem defteri karıştırırdı. */
              ipucu={paraBirimiSimgesi(paraBirimi)}
            />
          )}
          {tahsilat !== 'cari' && (
            <Secim
              etiket="Ödeme yöntemi" value={odemeTipi} onChange={setOdemeTipi}
              secenekler={ODEME_TIPLERI.map((t) => ({ deger: t, ad: ODEME_TIPI_ADI[t] ?? t }))}
            />
          )}
        </div>

        {/* ───── NE OLACAĞI, KAYDETMEDEN ÖNCE ─────
            Kullanıcının asıl sorusu buydu: "cariden ne kadar düşüyor".
            Cevabı kaydettikten sonra Müşteri ekranını açıp aramak zorunda
            kalmasın diye burada duruyor.

            SIRALAMA BİLEREK: ödeme seçiminin HEMEN ALTINDA, "Not"tan ÖNCE.
            İlk sürümde özet en sondaydı ve pencere yüksekliğinde alt
            kenarın altında kalıyordu — ekran görüntüsüyle ölçüldü: "Cariye
            yazılacak borç" ve "Satıştan sonra bakiye" satırları görünmüyor,
            kullanıcı asıl sorusunun cevabını görmek için kaydırmak zorunda
            kalıyordu. Not alanı bu üçünden daha az önemlidir. */}
        {toplamTL > 0 && (
          <div className="satis-ozet">
            <div className="satis-ozet-satir">
              <span>Satış toplamı</span>
              <strong>{para(toplamDoviz, paraBirimi)}
                {paraBirimi !== ANA_PARA_BIRIMI && <span className="ikincil"> · {para(toplamTL)}</span>}
              </strong>
            </div>
            <div className="satis-ozet-satir">
              <span>Kasaya girecek</span>
              <strong style={{ color: alinanTL > 0 ? 'var(--olumlu)' : undefined }}>
                {para(alinanDoviz, paraBirimi)}
              </strong>
            </div>
            {musteriId ? (
              <>
                <div className="satis-ozet-satir">
                  <span>Cariye yazılacak borç</span>
                  <strong style={{ color: cariyeTL > 0 ? 'var(--uyari)' : undefined }}>{para(cariyeTL)}</strong>
                </div>
                {bakiyeGorur && (
                  <div className="satis-ozet-satir satis-ozet-bakiye">
                    <span>Satıştan sonra bakiye</span>
                    <strong>
                      {para(kurus(mevcutBakiye + cariyeTL))}
                      <span className="ikincil"> ({para(mevcutBakiye)} idi)</span>
                    </strong>
                  </div>
                )}
              </>
            ) : (
              <div className="satis-ozet-satir ikincil">
                Tezgâh satışı — cariye hiçbir şey yazılmayacak
              </div>
            )}
          </div>
        )}

        <Girdi etiket="Not" value={not} onChange={setNot} placeholder="İsteğe bağlı" duzen="cumle" />

        <UyariSerit ton="notr">
          Stok {sayi(adetSayi)} {urun.birim} düşer.
          {musteriId && ' Satışın tamamı müşterinin hesabına borç yazılır; tahsil edilen kısım aynı anda tahsilat olarak düşülür.'}
        </UyariSerit>
        {hata && <UyariSerit ton="kritik">{hata}</UyariSerit>}
      </div>
    </Pencere>
  );
}
